import { useMemo } from 'react';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import type { RefundDrawerRow } from '../types';
import type { ReturnOrderListItemV2 } from 'services/api/refund';

export interface UseRefundDrawerBodyParams {
  trade: Conversation.Trade.Vo | undefined;
  platform: General.Platform;
  returnSn: string;
  row: RefundDrawerRow;
  statusTip: { date: string; money: string; platform: string };
  isRefund: boolean;
}

export interface RefundDrawerBodyStatusContent {
  title: string;
  description?: string;
}

export interface UseRefundStatusContentParams {
  platform: General.Platform;
  displayStatus: string;
  dueDateMs: string;
  unit: string;
  partiallyRefundAmount: string;
  statusTip: { date: string; money: string; platform: string };
  isRefund: boolean;
}

export const useRefundStatusContent = (props: UseRefundStatusContentParams) => {
  const { t } = useTranslate();

  const { platform, displayStatus, dueDateMs, unit, partiallyRefundAmount, statusTip, isRefund } =
    props;

  const statusContent = useMemo((): RefundDrawerBodyStatusContent | null => {
    if (platform === 'mercado') {
      const title = t(`mercado.returnStatus.${displayStatus}`);
      const description =
        displayStatus === 'REVERSE_PENDING' && !Number(dueDateMs)
          ? undefined
          : t(`mercado.returnDescription.${displayStatus}`, {
              date: dayjs(Number(dueDateMs)).format('YYYY-MM-DD'),
              amount: `${unit}${partiallyRefundAmount}`,
            });
      return { title, description };
    }
    if (platform !== 'lazada' && isRefund) {
      const shopeeDisplayStatus = displayStatus as Conversation.Trade.ShopeeDisplayStatus;
      const title = {
        IN_REVIEW: t('domain.shopeeRefund.displayStatusTitle.inReview'),
        WAITING_EVIDENCE: t('domain.shopeeRefund.displayStatusTitle.waitingEvidence'),
        RETURN_PROCESSING: t('domain.shopeeRefund.displayStatusTitle.returnProcessing'),
        PENDING_RESPOND: t('domain.shopeeRefund.displayStatusTitle.pendingRespond', {
          money: statusTip.money,
        }),
        REFUND_APPROVED: t('domain.shopeeRefund.displayStatusTitle.refundApproved'),
        WAITING_BUYER_RESPONSE: t('domain.shopeeRefund.displayStatusTitle.waitingBuyerResponse', {
          money: statusTip.money,
        }),
        RETURN_IN_PROGRESS: t('domain.shopeeRefund.displayStatusTitle.returnInProgress'),
        REFUND_COMPLETED: t('domain.shopeeRefund.displayStatusTitle.refundCompleted'),
        DISPUTE_RESOLVED: t('domain.shopeeRefund.displayStatusTitle.disputeResolved'),
        CANCELLED: t('domain.shopeeRefund.displayStatusTitle.cancelled'),
        UNKNOWN: t('domain.shopeeRefund.displayStatusTitle.unknown', { platform: 'Shopee' }),
        REFUND_PENDING: t('domain.shopeeRefund.displayStatusTitle.refundPending', {
          money: statusTip.money,
        }),
        RETURN_REFUND_PENDING: t('domain.shopeeRefund.displayStatusTitle.returnRefundPending', {
          money: statusTip.money,
        }),
        REFUND_REQUESTED: t('domain.shopeeRefund.displayStatusTitle.refundRequested', {
          money: statusTip.money,
        }),
        VALIDATION_IN_PROGRESS: t('domain.shopeeRefund.displayStatusTitle.validationInProgress'),
        VALIDATION_IN_PROGRESS_V2: t('domain.shopeeRefund.displayStatusTitle.validationInProgress'),
        EVIDENCE_SUBMITTED: t('domain.shopeeRefund.displayStatusTitle.evidenceSubmitted'),
        COMPENSATION_APPROVED: t('domain.shopeeRefund.displayStatusTitle.compensationApproved'),
        DISPUTE_REJECT: t('domain.shopeeRefund.displayStatusTitle.disputeReject'),
        DISPUTE_WAIT_EVIDENCE: t('domain.shopeeRefund.displayStatusTitle.disputeWaitEvidence'),
        REFUND_APPROVED_2: t('domain.shopeeRefund.displayStatusTitle.returnInProgress'),
      }[shopeeDisplayStatus];
      const description = {
        IN_REVIEW: t('domain.shopeeRefund.displayStatusDescription.inReview'),
        WAITING_EVIDENCE: t('domain.shopeeRefund.displayStatusDescription.waitingEvidence', {
          date: statusTip.date,
        }),
        RETURN_PROCESSING: t('domain.shopeeRefund.displayStatusDescription.returnProcessing', {
          date: statusTip.date,
        }),
        PENDING_RESPOND: t('domain.shopeeRefund.displayStatusDescription.pendingRespond', {
          money: statusTip.money,
          date: statusTip.date,
        }),
        REFUND_APPROVED: t('domain.shopeeRefund.displayStatusDescription.refundApproved', {
          money: statusTip.money,
          date: statusTip.date,
        }),
        WAITING_BUYER_RESPONSE: t(
          'domain.shopeeRefund.displayStatusDescription.waitingBuyerResponse',
        ),
        RETURN_IN_PROGRESS: t('domain.shopeeRefund.displayStatusDescription.returnInProgress'),
        REFUND_COMPLETED: t('domain.shopeeRefund.displayStatusDescription.refundCompleted', {
          money: statusTip.money,
        }),
        DISPUTE_RESOLVED: t('domain.shopeeRefund.displayStatusDescription.disputeResolved'),
        CANCELLED: t('domain.shopeeRefund.displayStatusDescription.cancelled'),
        UNKNOWN: t('domain.shopeeRefund.displayStatusDescription.unknown', { platform: 'Shopee' }),
        REFUND_PENDING: t('domain.shopeeRefund.displayStatusDescription.refundPending', {
          date: statusTip.date,
        }),
        RETURN_REFUND_PENDING: t(
          'domain.shopeeRefund.displayStatusDescription.returnRefundPending',
          {
            date: statusTip.date,
          },
        ),
        REFUND_REQUESTED: t('domain.shopeeRefund.displayStatusDescription.refundRequested', {
          date: statusTip.date,
        }),
        VALIDATION_IN_PROGRESS: t(
          'domain.shopeeRefund.displayStatusDescription.validationInProgress',
          {
            date: statusTip.date,
          },
        ),
        VALIDATION_IN_PROGRESS_V2: t(
          'scene.orderWorkspace.returnRefundTab.detail.hint.productArrivedAtWarehouse',
        ),
        EVIDENCE_SUBMITTED: t('domain.shopeeRefund.displayStatusDescription.evidenceSubmitted'),
        COMPENSATION_APPROVED: t(
          'domain.shopeeRefund.displayStatusDescription.compensationApproved',
          {
            money: statusTip.money,
          },
        ),
        DISPUTE_REJECT: t('domain.shopeeRefund.displayStatusDescription.disputeReject'),
        DISPUTE_WAIT_EVIDENCE: t(
          'domain.shopeeRefund.displayStatusDescription.disputeWaitEvidence',
          {
            date: statusTip.date,
          },
        ),
        REFUND_APPROVED_2: t('domain.shopeeRefund.displayStatusDescription.returnInProgress'),
      }[shopeeDisplayStatus];

      if (!title) return null;

      return { title, description };
    }
    return null;
  }, [
    platform,
    isRefund,
    t,
    displayStatus,
    dueDateMs,
    unit,
    partiallyRefundAmount,
    statusTip.money,
    statusTip.date,
  ]);

  return { statusContent };
};

/**
 * 退款抽屉 Body 内派生数据与状态文案。
 * 从 trade + dataSource + row 计算：退款商品列表、原因原始数据、展示订单号、状态区块文案。
 */
export function useRefundDrawerBody({
  trade,
  platform,
  returnSn,
  row,
  statusTip,
  isRefund,
}: UseRefundDrawerBodyParams) {
  const returnRow = row as ReturnOrderListItemV2;
  const { displayStatus, dueDateMs, unit = '', partiallyRefundAmount = '' } = returnRow;

  const { statusContent } = useRefundStatusContent({
    platform,
    displayStatus,
    dueDateMs,
    unit,
    partiallyRefundAmount,
    statusTip,
    isRefund,
  });

  const { refundItems, reasonRaw, displayTradeId, tradeItems } = useMemo(() => {
    if (!trade) {
      return {
        refundItems: [] as any[],
        reasonRaw: null as any,
        displayTradeId: '',
        tradeItems: [] as any[],
      };
    }
    const { items: itemsFromTrade = [], externalTradeId, invoiceNumber } = trade;
    const displayTradeId = platform === 'tokopedia' ? invoiceNumber : externalTradeId;
    const refundItems = itemsFromTrade
      .filter((it) => it.refundReason?.some((reason) => reason.returnSn === returnSn))
      .map(({ refundReason, ...others }) => ({
        ...others,
        refundReason: refundReason?.filter((_) => _.returnSn === returnSn),
      }));
    const reasonRaw = refundItems[0]?.refundReason
      ? refundItems[0].refundReason.find((r) => r.returnSn === returnSn) ?? null
      : null;
    return {
      refundItems,
      reasonRaw,
      displayTradeId: displayTradeId ?? '',
      tradeItems: itemsFromTrade,
    };
  }, [trade, platform, returnSn]);

  return {
    refundItems,
    reasonRaw,
    displayTradeId,
    tradeItems,
    statusContent,
  };
}
